/**
 * Friend Challenges Service
 * Create and manage challenges between friends
 * Feature #157
 */

import { getState, setState } from '../stores/state.js';
import { showToast } from './notifications.js';
import { escapeJSString } from '../utils/sanitize.js';
import { addPoints } from './gamification.js';
import { t } from '../i18n/index.js';

// ─── Firestore sync ─────────────────────────────────────────────────────────

async function syncChallengeToFirestore(challenge) {
  try {
    const fb = await import('./firebase.js')
    const db = fb.getDb()
    const currentUser = fb.getCurrentUser()
    if (!db || !currentUser) return
    const { id, ...data } = challenge
    await fb.setDoc(fb.doc(db, 'friendChallenges', id), data, { merge: true })
  } catch (e) {
    console.warn('[FriendChallenges] Firestore sync failed:', e.message)
  }
}


/**
 * Challenge types available
 */
// Challenge type definitions — names resolved lazily via t() for i18n
const challengeTypeDefs = [
  { id: 'checkins_race', nameKey: 'friendChallengeCheckinsRaceName', descKey: 'friendChallengeCheckinsRaceDesc', nameEn: 'Check-in Race', descriptionEn: 'First to complete X check-ins wins', iconName: 'flag-checkered', metric: 'checkins', defaultTarget: 10, minTarget: 5, maxTarget: 50, rewardPoints: 100 },
  { id: 'spots_discovery', nameKey: 'friendChallengeSpotsDiscoveryName', descKey: 'friendChallengeSpotsDiscoveryDesc', nameEn: 'Spot Discovery', descriptionEn: 'First to visit X new spots wins', iconName: 'map', metric: 'spotsVisited', defaultTarget: 5, minTarget: 3, maxTarget: 20, rewardPoints: 150 },
  { id: 'countries_explored', nameKey: 'friendChallengeCountriesExploredName', descKey: 'friendChallengeCountriesExploredDesc', nameEn: 'World Tour', descriptionEn: 'First to visit X countries wins', iconName: 'globe', metric: 'countriesVisited', defaultTarget: 3, minTarget: 2, maxTarget: 10, rewardPoints: 300 },
  { id: 'reviews_battle', nameKey: 'friendChallengeReviewsBattleName', descKey: 'friendChallengeReviewsBattleDesc', nameEn: 'Review Battle', descriptionEn: 'First to give X reviews wins', iconName: 'pen-line', metric: 'reviewsGiven', defaultTarget: 10, minTarget: 5, maxTarget: 30, rewardPoints: 120 },
  { id: 'distance_race', nameKey: 'friendChallengeDistanceRaceName', descKey: 'friendChallengeDistanceRaceDesc', nameEn: 'Distance Race', descriptionEn: 'First to travel X km hitchhiking wins', iconName: 'car', metric: 'totalDistance', defaultTarget: 500, minTarget: 100, maxTarget: 2000, rewardPoints: 250 },
  { id: 'night_hitchhiker', nameKey: 'friendChallengeNightHitchhikerName', descKey: 'friendChallengeNightHitchhikerDesc', nameEn: 'Night Hitchhiker', descriptionEn: 'First to complete X night check-ins wins', iconName: 'moon', metric: 'nightCheckins', defaultTarget: 3, minTarget: 1, maxTarget: 10, rewardPoints: 180 },
]

export function getChallengeTypes() {
  return challengeTypeDefs.map(d => ({
    ...d,
    name: t(d.nameKey),
    description: t(d.descKey),
  }))
}

// Backward-compatible export — Proxy that delegates all array operations to getChallengeTypes()
export const challengeTypes = new Proxy(challengeTypeDefs, {
  get(target, prop) {
    const types = getChallengeTypes()
    if (prop === 'length') return types.length
    if (typeof prop === 'string' && !isNaN(prop)) return types[Number(prop)]
    if (typeof types[prop] === 'function') return types[prop].bind(types)
    return types[prop]
  }
})

/**
 * Challenge status enum
 */
export const ChallengeStatus = {
  PENDING: 'pending', // Waiting for friend to accept
  ACTIVE: 'active', // Challenge is ongoing
  COMPLETED: 'completed', // Someone won
  EXPIRED: 'expired', // Time limit reached
  DECLINED: 'declined', // Friend declined
  CANCELLED: 'cancelled', // Creator cancelled
};

/**
 * Create a new challenge
 * @param {string} friendId - Friend's user ID
 * @param {string} challengeTypeId - Type of challenge
 * @param {number} target - Target to reach
 * @param {number} durationDays - Duration in days (default 7)
 * @returns {Object} Created challenge
 */
export function createChallenge(friendId, challengeTypeId, target, durationDays = 7) {
  const state = getState();
  const challengeType = challengeTypes.find(c => c.id === challengeTypeId);

  if (!challengeType) {
    showToast(t('friendChallengeInvalidType') || 'Type de defi invalide', 'error');
    return null;
  }

  // Validate target
  const validTarget = Math.max(
    challengeType.minTarget,
    Math.min(challengeType.maxTarget, target || challengeType.defaultTarget)
  );

  const challenge = {
    id: `challenge_${Date.now()}_${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
    type: challengeTypeId,
    creatorId: state.user?.uid || 'local',
    creatorName: state.username || t('me') || 'Moi',
    friendId,
    friendName: getFriendName(friendId),
    target: validTarget,
    creatorProgress: 0,
    friendProgress: 0,
    status: ChallengeStatus.PENDING,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString(),
    winnerId: null,
    rewardPoints: challengeType.rewardPoints,
  };

  // Add to state
  const challenges = state.friendChallenges || [];
  setState({
    friendChallenges: [...challenges, challenge],
  });

  // Sync to Firestore
  syncChallengeToFirestore(challenge)

  showToast((t('friendChallengeSent') || 'Defi envoye a {name} !').replace('{name}', challenge.friendName), 'success');
  return challenge;
}

/**
 * Accept a challenge
 * @param {string} challengeId - Challenge ID
 */
export function acceptChallenge(challengeId) {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const index = challenges.findIndex(c => c.id === challengeId);

  if (index === -1) {
    showToast(t('challengeNotFound') || 'Defi introuvable', 'error');
    return false;
  }

  const challenge = challenges[index];
  if (challenge.status !== ChallengeStatus.PENDING) {
    showToast(t('friendChallengeNotPending') || 'Ce defi n\'est plus en attente', 'error');
    return false;
  }

  challenges[index] = {
    ...challenge,
    status: ChallengeStatus.ACTIVE,
    startedAt: new Date().toISOString(),
  };

  setState({ friendChallenges: [...challenges] });
  syncChallengeToFirestore(challenges[index])
  showToast(t('friendChallengeAccepted') || 'Defi accepte ! Que le meilleur gagne !', 'success');
  return true;
}

/**
 * Decline a challenge
 * @param {string} challengeId - Challenge ID
 */
export function declineChallenge(challengeId) {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const index = challenges.findIndex(c => c.id === challengeId);

  if (index === -1) return false;

  challenges[index] = {
    ...challenges[index],
    status: ChallengeStatus.DECLINED,
  };

  setState({ friendChallenges: [...challenges] });
  syncChallengeToFirestore(challenges[index])
  showToast(t('friendChallengeDeclined') || 'Defi decline', 'info');
  return true;
}

/**
 * Cancel a challenge (creator only)
 * @param {string} challengeId - Challenge ID
 */
export function cancelChallenge(challengeId) {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const index = challenges.findIndex(c => c.id === challengeId);

  if (index === -1) return false;

  const challenge = challenges[index];
  if (challenge.creatorId !== (state.user?.uid || 'local')) {
    showToast(t('friendChallengeCreatorOnly') || 'Seul le createur peut annuler ce defi', 'error');
    return false;
  }

  challenges[index] = {
    ...challenge,
    status: ChallengeStatus.CANCELLED,
  };

  setState({ friendChallenges: [...challenges] });
  syncChallengeToFirestore(challenges[index])
  showToast(t('friendChallengeCancelled') || 'Defi annule', 'info');
  return true;
}

/**
 * Update challenge progress
 * @param {string} challengeId - Challenge ID
 * @param {string} participantId - 'creator' or 'friend'
 * @param {number} progress - New progress value
 */
export function updateChallengeProgress(challengeId, participantId, progress) {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const index = challenges.findIndex(c => c.id === challengeId);

  if (index === -1) return false;

  const challenge = challenges[index];
  if (challenge.status !== ChallengeStatus.ACTIVE) return false;

  const progressKey = participantId === 'creator' ? 'creatorProgress' : 'friendProgress';
  const newProgress = Math.max(0, progress);

  challenges[index] = {
    ...challenge,
    [progressKey]: newProgress,
  };

  // Check if someone won
  if (newProgress >= challenge.target) {
    challenges[index].status = ChallengeStatus.COMPLETED;
    challenges[index].winnerId = participantId === 'creator' ? challenge.creatorId : challenge.friendId;
    challenges[index].completedAt = new Date().toISOString();

    // Award points to winner
    const isWinner = participantId === 'creator'
      ? challenge.creatorId === (state.user?.uid || 'local')
      : challenge.friendId === (state.user?.uid || 'local');

    if (isWinner) {
      addPoints(challenge.rewardPoints, 'friend_challenge_won');
      showToast((t('friendChallengeWon') || 'Tu as gagne le defi ! +{points} pts').replace('{points}', challenge.rewardPoints), 'success');
    } else {
      showToast(t('friendChallengeLost') || 'Ton ami a gagne le defi !', 'info');
    }
  }

  setState({ friendChallenges: [...challenges] });
  return true;
}

/**
 * Auto-update progress based on user stats
 */
export function syncChallengeProgress() {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const userId = state.user?.uid || 'local';

  let updated = false;

  challenges.forEach((challenge, index) => {
    if (challenge.status !== ChallengeStatus.ACTIVE) return;

    // Check expiration
    if (new Date(challenge.expiresAt) < new Date()) {
      challenges[index] = {
        ...challenge,
        status: ChallengeStatus.EXPIRED,
      };
      updated = true;
      return;
    }

    // Update progress based on challenge type
    const challengeType = challengeTypes.find(c => c.id === challenge.type);
    if (!challengeType) return;

    const isCreator = challenge.creatorId === userId;
    const currentProgress = isCreator ? challenge.creatorProgress : challenge.friendProgress;
    let newProgress = currentProgress;

    // Get metric from state
    switch (challengeType.metric) {
      case 'checkins':
        newProgress = state.checkins || 0;
        break;
      case 'spotsVisited':
        newProgress = (state.visitedSpots || []).length;
        break;
      case 'countriesVisited':
        newProgress = state.countriesVisited || 0;
        break;
      case 'reviewsGiven':
        newProgress = state.reviewsGiven || 0;
        break;
      case 'totalDistance':
        newProgress = state.totalDistance || 0;
        break;
      case 'nightCheckins':
        newProgress = state.nightCheckins || 0;
        break;
    }

    if (newProgress !== currentProgress) {
      updateChallengeProgress(challenge.id, isCreator ? 'creator' : 'friend', newProgress);
      updated = true;
    }
  });

  if (updated) {
    setState({ friendChallenges: [...challenges] });
  }
}

/**
 * Get active challenges
 * @returns {Object[]} Active challenges
 */
export function getActiveChallenges() {
  const state = getState();
  return (state.friendChallenges || []).filter(c => c.status === ChallengeStatus.ACTIVE);
}

/**
 * Get pending challenges (sent or received)
 * @returns {Object[]} Pending challenges
 */
export function getPendingChallenges() {
  const state = getState();
  return (state.friendChallenges || []).filter(c => c.status === ChallengeStatus.PENDING);
}

/**
 * Get completed challenges
 * @returns {Object[]} Completed challenges
 */
export function getCompletedChallenges() {
  const state = getState();
  return (state.friendChallenges || []).filter(c => c.status === ChallengeStatus.COMPLETED);
}

/**
 * Get challenge statistics
 * @returns {Object} Stats object
 */
export function getChallengeStats() {
  const state = getState();
  const challenges = state.friendChallenges || [];
  const userId = state.user?.uid || 'local';

  const completed = challenges.filter(c => c.status === ChallengeStatus.COMPLETED);
  const wins = completed.filter(c => c.winnerId === userId).length;
  const losses = completed.filter(c => c.winnerId && c.winnerId !== userId).length;

  return {
    total: challenges.length,
    active: challenges.filter(c => c.status === ChallengeStatus.ACTIVE).length,
    pending: challenges.filter(c => c.status === ChallengeStatus.PENDING).length,
    completed: completed.length,
    wins,
    losses,
    winRate: completed.length > 0 ? Math.round((wins / completed.length) * 100) : 0,
  };
}

// getChallengeTypes is defined above with lazy i18n resolution

/**
 * Helper to get friend name from ID
 * @param {string} friendId - Friend ID
 * @returns {string} Friend name
 */
function getFriendName(friendId) {
  const state = getState();
  const friend = (state.friends || []).find(f => f.id === friendId);
  return friend?.name || friend?.username || t('friend') || 'Ami';
}

/**
 * Render challenge card HTML
 * @param {Object} challenge - Challenge object
 * @returns {string} HTML string
 */
export function renderChallengeCard(challenge) {
  const state = getState();
  const userId = state.user?.uid || 'local';
  const challengeType = challengeTypes.find(c => c.id === challenge.type);
  const isCreator = challenge.creatorId === userId;
  const myProgress = isCreator ? challenge.creatorProgress : challenge.friendProgress;
  const opponentProgress = isCreator ? challenge.friendProgress : challenge.creatorProgress;
  const opponentName = isCreator ? challenge.friendName : challenge.creatorName;

  const progressPercent = Math.min(100, Math.round((myProgress / challenge.target) * 100));
  const opponentPercent = Math.min(100, Math.round((opponentProgress / challenge.target) * 100));

  const statusColors = {
    [ChallengeStatus.PENDING]: 'bg-yellow-500/20 text-yellow-400',
    [ChallengeStatus.ACTIVE]: 'bg-green-500/20 text-green-400',
    [ChallengeStatus.COMPLETED]: 'bg-blue-500/20 text-blue-400',
    [ChallengeStatus.EXPIRED]: 'bg-slate-500/20 text-slate-400',
    [ChallengeStatus.DECLINED]: 'bg-red-500/20 text-red-400',
    [ChallengeStatus.CANCELLED]: 'bg-slate-500/20 text-slate-400',
  };

  const statusLabels = {
    [ChallengeStatus.PENDING]: t('friendChallengeStatusPending') || 'En attente',
    [ChallengeStatus.ACTIVE]: t('friendChallengeStatusActive') || 'En cours',
    [ChallengeStatus.COMPLETED]: challenge.winnerId === userId ? (t('friendChallengeStatusWon') || 'Gagne !') : (t('friendChallengeStatusLost') || 'Perdu'),
    [ChallengeStatus.EXPIRED]: t('friendChallengeStatusExpired') || 'Expire',
    [ChallengeStatus.DECLINED]: t('friendChallengeStatusDeclined') || 'Decline',
    [ChallengeStatus.CANCELLED]: t('friendChallengeStatusCancelled') || 'Annule',
  };

  return `
    <div class="bg-dark-primary rounded-xl p-4 border border-white/10">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <span class="text-2xl">${challengeType?.icon || 'target'}</span>
          <div>
            <h4 class="font-semibold text-white">${challengeType?.name || challenge.type}</h4>
            <p class="text-xs text-slate-400">vs ${opponentName}</p>
          </div>
        </div>
        <span class="px-2 py-1 rounded-full text-xs ${statusColors[challenge.status]}">
          ${statusLabels[challenge.status]}
        </span>
      </div>

      ${challenge.status === ChallengeStatus.ACTIVE ? `
        <div class="space-y-2 mb-3">
          <div class="flex items-center gap-2">
            <span class="text-xs text-slate-400 w-12">${t('you') || 'Toi'}</span>
            <div class="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
              <div class="bg-primary h-full transition-colors" style="width: ${progressPercent}%"></div>
            </div>
            <span class="text-xs text-white w-10 text-right">${myProgress}/${challenge.target}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-slate-400 w-12 truncate">${opponentName.substring(0, 5)}</span>
            <div class="flex-1 bg-white/5 rounded-full h-2 overflow-hidden">
              <div class="bg-red-500 h-full transition-colors" style="width: ${opponentPercent}%"></div>
            </div>
            <span class="text-xs text-white w-10 text-right">${opponentProgress}/${challenge.target}</span>
          </div>
        </div>
        <p class="text-xs text-slate-400">
          ${(t('friendChallengeExpiresOn') || 'Expire le {date}').replace('{date}', new Date(challenge.expiresAt).toLocaleDateString())}
        </p>
      ` : ''}

      ${challenge.status === ChallengeStatus.PENDING && !isCreator ? `
        <div class="flex gap-2 mt-3">
          <button onclick="window.acceptFriendChallenge('${escapeJSString(challenge.id)}')"
            class="flex-1 bg-primary text-white py-2 rounded-xl text-sm hover:bg-primary/80">
            ${t('accept') || 'Accepter'}
          </button>
          <button onclick="window.declineFriendChallenge('${escapeJSString(challenge.id)}')"
            class="flex-1 bg-white/5 text-slate-300 py-2 rounded-xl text-sm hover:bg-white/10">
            ${t('decline') || 'Decliner'}
          </button>
        </div>
      ` : ''}

      ${challenge.status === ChallengeStatus.PENDING && isCreator ? `
        <div class="mt-3">
          <button onclick="window.cancelFriendChallenge('${escapeJSString(challenge.id)}')"
            class="w-full bg-white/5 text-slate-300 py-2 rounded-xl text-sm hover:bg-white/10">
            ${t('cancel') || 'Annuler'}
          </button>
        </div>
      ` : ''}

      ${challenge.status === ChallengeStatus.COMPLETED && challenge.winnerId === userId ? `
        <p class="text-center text-green-400 font-medium mt-2">
          +${challenge.rewardPoints} ${(t('pointsEarned') || 'pouces gagnes !')}
        </p>
      ` : ''}
    </div>
  `;
}

export default {
  challengeTypes,
  ChallengeStatus,
  createChallenge,
  acceptChallenge,
  declineChallenge,
  cancelChallenge,
  updateChallengeProgress,
  syncChallengeProgress,
  getActiveChallenges,
  getPendingChallenges,
  getCompletedChallenges,
  getChallengeStats,
  getChallengeTypes,
  renderChallengeCard,
};
